import React, { useCallback, useEffect, useRef, useState } from 'react'
import Table from '../UI/CommonTable/CommonTable';
import AssignDriverModal from '../../pages/Order/AssignDriverModal';
import { IOrder } from '../../interfaces/order';
import StatusDropDown from './OrderStatusDropdown';
import { API_ENDPOINT, API_URL, API_URL_2 } from '../../api/constant';
import { callApi } from '../../api/callApi';
import { useNavigate } from 'react-router-dom';
import { ISettings } from '../../interfaces/settings';
import { IAddress } from '../../interfaces/address';
import { formatDateTime } from '../../utility/helper';
import moment from '../../utility/moment-timezone';
const POLL_INTERVAL_MS = 2 * 60 * 1000;

const RecentOrders: React.FC<{ isOrderMoniter?: boolean, selectedStatus?: string }> = ({ isOrderMoniter = false, selectedStatus }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [orders, SetOrders] = useState<IOrder[]>([]);
  const [settings, setSettings] = useState<ISettings | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [driverModal, setDriverModal] = useState(false);

  const navigate = useNavigate();
  const intervalRef = useRef<number | null>(null);
  const reqIdRef = useRef<number>(0);

  useEffect(() => {
    if (!settings) fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await callApi(API_URL + API_ENDPOINT.auth + 'settings', { method: 'GET' });
      if (response && response.length > 0) {
        let setting = response[0];
        setSettings(setting);
      }
    } catch (error: any) {
      console.error('Failed to fetch settings:', error.message);
    }
  };

  const getOrders = useCallback(async (page: number = 1) => {
    const currentReqId = ++reqIdRef.current;
    try {
      setLoading(true);
      const params = isOrderMoniter ? (selectedStatus && selectedStatus !== 'All' ? `status=${selectedStatus}` : ``) : 'isRecent=true&limit=5';
      const response = await callApi(API_URL_2 + API_ENDPOINT.order + `orders?page=${page}&sortType=DESC&${params}`, { method: 'GET' });
      if (currentReqId === reqIdRef.current) {
        const { data } = response;
        SetOrders(data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch orders:', error?.message || error);
    } finally {
      if (currentReqId === reqIdRef.current) setLoading(false);
    }
  }, [isOrderMoniter, selectedStatus]);

  const startPolling = useCallback(() => {
    if (intervalRef.current !== null) return;
    intervalRef.current = window.setInterval(() => {
      getOrders(1);
    }, POLL_INTERVAL_MS);
  }, [getOrders]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        getOrders(1);
        startPolling();
      } else {
        stopPolling();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    if (document.visibilityState === 'visible') {
      getOrders(1);
      startPolling();
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPolling();
    };
  }, [getOrders, startPolling, stopPolling]);

  useEffect(() => {
    getOrders(1);
    stopPolling();
    if (document.visibilityState === 'visible') startPolling();
  }, [selectedStatus, isOrderMoniter]);

  const columns = [
    { header: "Order ID", accessor: "orderId" },
    {
      header: "Status",
      accessor: "status",
      render: (status: string, order: IOrder) =>
        <StatusDropDown
          CurrentStatus={status}
          orderId={order.id}
          orderStatus={settings?.orderStatus || []}
          previousStatus={order.previousStatus}
          updateStatus={() => getOrders(1)}
        />
    },
    { header: 'Delivery Time', accessor: 'deliveryTime', render: (deliveryTime: string) => formatDateTime(deliveryTime) },
    { header: "Total", accessor: "total" },
    { header: "Discount", accessor: "discountedValue" },
    { header: "Grand Total", accessor: "grandTotal" },
    {
      header: "Delivery Address",
      accessor: "address",
      render: (address: IAddress) => {
        const addressLine = [
          address.buildingName,
          address.aptNumber,
          address.floor,
          address.street,
          address.block,
          address.additionalDirection,
          address.phone,
        ]
          .filter((item) => item && item.trim() !== "")
          .join(", ");
        return addressLine;
      }
    },
    { header: "Created At", accessor: "createdAt", render: (createdAt: string) => moment(createdAt).format("DD MMM, YYYY hh:mm a") },
  ];

  const actions = [
    {
      icon: "bi bi-eye",
      color: "success",
      onClick: (order: IOrder) => navigate(`/order-details/${order.id}`),
      tooltip: 'View Order'
    },
  ];

  return (
    <>
      <div className='d-flex justify-content-between align-items-start'>
        <h1 className="app-page-title">{isOrderMoniter ? selectedStatus : 'Recent'} Orders</h1>
      </div>
      <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
        <div className="app-card-body">
          <Table
            columns={columns}
            data={orders}
            loading={loading}
            actions={actions}
            emptyMessage="No Orders available."
          />
        </div>
      </div>
      <AssignDriverModal modal={driverModal} close={() => {
        setDriverModal(false);
        setSelectedOrders([]);
        getOrders(1);
      }} selectedOrders={selectedOrders} />
    </>
  );
};

export default RecentOrders;
